import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';
import ExportButton from '../common/ExportButton';
import { exportToCSV, exportToExcel, exportToPDF } from '../../utils/exportUtils';
import { IoStatsChart, IoReceiptOutline, IoCashOutline } from 'react-icons/io5';

const ReporteProveedores = ({ month, year }) => {
    const { userData } = useAuth();
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totals, setTotals] = useState({
        amount: 0,
        invoices: 0
    });

    useEffect(() => {
        if (!userData?.condominioId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // Period: Start to End of selected month
                const startDate = moment().year(year).month(month).startOf('month');
                const endDate = moment().year(year).month(month).endOf('month');

                const q = query(
                    collection(db, "gastos"),
                    where("condominioId", "==", userData.condominioId),
                    where("tipo", "==", "egreso")
                );

                const snap = await getDocs(q);

                const providerMap = {};
                let totalAmount = 0;
                let totalInvoices = 0;

                snap.forEach(doc => {
                    const gasto = doc.data();
                    const fecha = gasto.fecha?.toDate ? moment(gasto.fecha.toDate()) : moment();

                    // Filter by Date Range
                    if (!fecha.isBetween(startDate, endDate, 'day', '[]')) return;

                    // Filter out rejected and draft expenses
                    if (gasto.estatus === 'rechazado' || gasto.estatus === 'borrador') return;

                    const providerName = gasto.proveedor || 'Proveedor Desconocido';
                    const amount = parseFloat(gasto.monto || 0);

                    if (!providerMap[providerName]) {
                        providerMap[providerName] = {
                            provider: providerName,
                            amount: 0,
                            count: 0
                        };
                    }

                    providerMap[providerName].amount += amount;
                    providerMap[providerName].count += 1;

                    totalAmount += amount;
                    totalInvoices += 1;
                });

                // Calculate Percentages and Convert to Array
                const result = Object.values(providerMap).map(p => ({
                    ...p,
                    percentage: totalAmount > 0 ? (p.amount / totalAmount) * 100 : 0
                }));

                // Sort by Amount Descending
                result.sort((a, b) => b.amount - a.amount);

                setReportData(result);
                setTotals({ amount: totalAmount, invoices: totalInvoices });

            } catch (error) {
                console.error("Error generating Vendor Report:", error);
            }
            setLoading(false);
        };

        fetchData();
    }, [userData, month, year]);

    const getExportData = () => {
        return reportData.map(row => ({
            'Proveedor': row.provider,
            'Facturas': row.count,
            'Total Gastado': row.amount,
            '% del Total': `${row.percentage.toFixed(2)}%`
        }));
    };

    const handleExportCSV = () => {
        exportToCSV(getExportData(), `Reporte_Proveedores_${moment().month(month).format('MMMM')}_${year}`);
    };

    const handleExportExcel = () => {
        exportToExcel(getExportData(), `Reporte_Proveedores_${moment().month(month).format('MMMM')}_${year}`, 'Proveedores', userData?.condominioNombre);
    };

    const handleExportPDF = () => {
        const columns = ['Proveedor', 'Facturas', 'Total Gastado', '% del Total'];
        const data = reportData.map(row => [
            row.provider,
            row.count,
            `$${new Intl.NumberFormat('es-MX').format(row.amount)}`,
            `${row.percentage.toFixed(2)}%`
        ]);

        // Add totals
        data.push([
            'TOTALES',
            totals.invoices,
            `$${new Intl.NumberFormat('es-MX').format(totals.amount)}`,
            '100%'
        ]);

        exportToPDF(
            `Análisis de Proveedores - ${moment().month(month).format('MMMM')} ${year}`,
            columns,
            data,
            `Reporte_Proveedores_${year}_${month + 1}`,
            userData?.condominioNombre
        );
    };

    if (loading) return <div className="loading-state">Calculando análisis de proveedores...</div>;

    return (
        <div className="report-container fade-in">
            <div className="report-header-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '20px' }}>
                    <div className="kpi-card-mini" style={{ background: '#f8f9fa', padding: '10px 20px', borderRadius: '8px', borderLeft: '4px solid #2563eb' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <IoCashOutline style={{ fontSize: '1.2em', color: '#2563eb' }} />
                            <div>
                                <span style={{ fontSize: '0.8em', color: '#666', display: 'block' }}>Total Gastado</span>
                                <h3 style={{ margin: 0, color: '#333' }}>
                                    ${new Intl.NumberFormat('es-MX').format(totals.amount)}
                                </h3>
                            </div>
                        </div>
                    </div>
                    <div className="kpi-card-mini" style={{ background: '#f8f9fa', padding: '10px 20px', borderRadius: '8px', borderLeft: '4px solid #6366f1' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <IoReceiptOutline style={{ fontSize: '1.2em', color: '#6366f1' }} />
                            <div>
                                <span style={{ fontSize: '0.8em', color: '#666', display: 'block' }}>Facturas Procesadas</span>
                                <h3 style={{ margin: 0, color: '#333' }}>{totals.invoices}</h3>
                            </div>
                        </div>
                    </div>
                </div>

                <ExportButton
                    onExportCSV={handleExportCSV}
                    onExportExcel={handleExportExcel}
                    onExportPDF={handleExportPDF}
                />
            </div>

            <div className="table-responsive">
                <table className="styled-table">
                    <thead>
                        <tr>
                            <th style={{ width: '50px' }}>#</th>
                            <th>Proveedor</th>
                            <th style={{ textAlign: 'center' }}>Facturas</th>
                            <th style={{ textAlign: 'right' }}>Total Gastado</th>
                            <th style={{ textAlign: 'right' }}>% del Total</th>
                            <th>Participación</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.length === 0 ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No hay gastos registrados en este periodo.</td></tr>
                        ) : reportData.map((row, idx) => (
                            <tr key={idx}>
                                <td style={{ color: '#999', fontSize: '0.9em' }}>{idx + 1}</td>
                                <td style={{ fontWeight: '500', color: '#374151' }}>{row.provider}</td>
                                <td style={{ textAlign: 'center' }}>{row.count}</td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>${new Intl.NumberFormat('es-MX').format(row.amount)}</td>
                                <td style={{ textAlign: 'right' }}>{row.percentage.toFixed(1)}%</td>
                                <td style={{ padding: '10px' }}>
                                    <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '8px', width: '100%', overflow: 'hidden' }}>
                                        <div style={{
                                            background: idx === 0 ? '#2563eb' : (idx < 3 ? '#60a5fa' : '#9ca3af'),
                                            width: `${row.percentage}%`,
                                            height: '100%'
                                        }}></div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {/* Totals Row */}
                        <tr style={{ backgroundColor: '#f3f4f6', fontWeight: 'bold' }}>
                            <td colSpan="2" style={{ textAlign: 'right' }}>Totales:</td>
                            <td style={{ textAlign: 'center' }}>{totals.invoices}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.amount)}</td>
                            <td style={{ textAlign: 'right' }}>100%</td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {/* Top Spend Insight */}
                {reportData.length > 0 && (
                    <div className="insight-card" style={{ background: '#eff6ff', padding: '15px', borderRadius: '8px', flex: 1, border: '1px solid #bfdbfe' }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <IoStatsChart /> Proveedor Principal
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.9em', color: '#1e3a8a' }}>
                            <strong>{reportData[0].provider}</strong> representa el <strong>{reportData[0].percentage.toFixed(1)}%</strong> de tus gastos totales este mes.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReporteProveedores;
